import React from 'react';
import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';
import { submitQueriesStream } from '@services/genaiQueriesV2';
import { addAdvancedSettingValues } from '@hooks/application/useAdvancedSettings';
import { logError, logFormSubmit, logger } from '@utils/usefulFuncs';
import { AdvancedSettingsContext } from '@/contexts/AdvancedSettingsContext';
import { ModeContext } from '@/contexts/ModeContext';
import { QueryControllerContext } from '@/contexts/QueryControllerContext';
import { ReasoningChatContext } from './ReasoningChatContext';
import { parseReasoningResult } from '@features/querySeries/utils/parseReasoningResult';

const buildExtraContext = (question, answer, reasoning, messages) => {
  let context = `User: ${question}`;
  if (answer) context += `\nAssistant: ${answer}`;
  if (reasoning) context += `\nReasoning: ${reasoning}`;

  for (const msg of messages) {
    if (msg.isOriginal) continue;
    if (msg.role === 'user') {
      context += `\nUser: ${msg.text}`;
    } else if (msg.role === 'assistant' && msg.answer) {
      context += `\nAssistant: ${msg.answer}`;
      if (msg.reasoning) context += `\nReasoning: ${msg.reasoning}`;
    }
  }
  return context;
};

export const useReasoningChat = () => {
  const advancedSettingsValues = React.useContext(AdvancedSettingsContext);
  const { promptSuffix, tool } = React.useContext(ModeContext);
  const { prevQueryFormData } = React.useContext(QueryControllerContext);
  const {
    question,
    answer,
    reasoning,
    messages,
    addUserMessage,
    addAssistantMessage,
    updateLastAssistantMessage,
    finalizeLastAssistantMessage,
    setIsStreaming,
    setError,
  } = React.useContext(ReasoningChatContext);

  const sourceRef = React.useRef(null);

  const cancelStream = React.useCallback(() => {
    if (sourceRef.current) {
      sourceRef.current.close();
      sourceRef.current = null;
    }
  }, []);

  React.useEffect(() => cancelStream, [cancelStream]);

  const sendMessage = React.useCallback(
    async (text) => {
      if (!text.trim()) return;

      const userText = text.trim();
      addUserMessage(userText);
      addAssistantMessage();
      setIsStreaming(true);
      setError(null);

      const questionWithSuffix = promptSuffix ? `${userText} ${promptSuffix}` : userText;
      const rawDocs = prevQueryFormData?.selectedDocs || prevQueryFormData?.doc_list || [];
      const docList = rawDocs.map((doc) => (typeof doc === 'object' ? doc.id : doc));
      const extraContext = buildExtraContext(question, answer, reasoning, messages);

      const settings = { user_mode: tool, extra_context: extraContext };
      addAdvancedSettingValues(advancedSettingsValues, settings);
      logFormSubmit({ ...settings, question: questionWithSuffix, doc_ids: docList });
      const result = await submitQueriesStream({
        questions: questionWithSuffix,
        docIds: docList.length > 0 ? docList : undefined,
        settings,
      });
      if (result.status !== 200) {
        setIsStreaming(false);
        setError(result.error || 'Something went wrong');
        return;
      }
      const resultId = result.runId;

      if (!resultId) {
        setIsStreaming(false);
        setError('Missing result id from response');
        return;
      }

      cancelStream();

      const streamUrl = `${BASEURL}/api/v2/genai/queries/stream_events?run_id=${encodeURIComponent(resultId)}&include_context=true`;
      const source = createAuthedEventSource(streamUrl);
      sourceRef.current = source;

      source.addEventListener('message', (event) => {
        try {
          const streamData = JSON.parse(event?.data || '{}');
          const answerText = streamData?.answer || '';
          logger('useReasoningChat onMessage', 'received', {
            runId: resultId,
            questionIndex: streamData?.question_index,
            finalAnswer: streamData?.final_answer === true,
            answerLength: answerText.length,
          });
          if (answerText.length > 0) {
            const parsed = parseReasoningResult(answerText);
            updateLastAssistantMessage(answerText, parsed);
          }
        } catch (e) {
          logError('Reasoning chat stream message error', e);
        }
      });

      source.addEventListener('complete', (event) => {
        try {
          const streamData = JSON.parse(event?.data || '{}');
          const answers = streamData?.response?.answers || [];
          const row = answers.find((r) => r?.final_answer) || answers[0];
          const answerText = row?.answer || '';
          const parsed = parseReasoningResult(answerText);
          finalizeLastAssistantMessage(parsed?.answer || '', parsed?.reasoning || '');
        } catch (e) {
          logError('Reasoning chat stream complete parse error', e);
        } finally {
          setIsStreaming(false);
          source.close();
          sourceRef.current = null;
        }
      });

      source.addEventListener('error', (e) => {
        logError('Reasoning chat stream error', e);
        setIsStreaming(false);
        setError('Streaming failed');
        source.close();
        sourceRef.current = null;
      });
    },
    [
      question,
      answer,
      reasoning,
      messages,
      promptSuffix,
      tool,
      prevQueryFormData,
      advancedSettingsValues,
      addUserMessage,
      addAssistantMessage,
      updateLastAssistantMessage,
      finalizeLastAssistantMessage,
      setIsStreaming,
      setError,
      cancelStream,
    ]
  );

  return { sendMessage, cancelStream };
};
